import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Component } from "react";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { ThemeProvider } from "@/lib/theme-context";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Dashboard from "@/pages/Dashboard";
import Workspace from "@/pages/Workspace";
import ProjectDetail from "@/pages/ProjectDetail";
import AgileBoard from "@/pages/AgileBoard";
import RaidPage from "@/pages/RaidPage";
import Meetings from "@/pages/Meetings";
import Sprints from "@/pages/Sprints";
import Dependencies from "@/pages/Dependencies";
import Gantt from "@/pages/Gantt";
import ProjectReport from "@/pages/ProjectReport";
import Reporting from "@/pages/Reporting";
import Templates from "@/pages/Templates";
import Deals from "@/pages/Deals";
import Assistant from "@/pages/Assistant";
import Team from "@/pages/Team";
import Capacity from "@/pages/Capacity";
import InviteAccept from "@/pages/InviteAccept";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import AgingReport from "@/pages/AgingReport";
import RecurringInvoices from "@/pages/RecurringInvoices";
import ProjectChat from "@/pages/ProjectChat";
import MyTasks from "@/pages/MyTasks";
import Marketplace from "@/pages/Marketplace";
import Integrations from "@/pages/Integrations";
import Wiki from "@/pages/Wiki";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import Checkout from "@/pages/Checkout";
import Portfolio from "@/pages/Portfolio";
import Governance from "@/pages/Governance";
import Contacts from "@/pages/Contacts";
import Compliance from "@/pages/Compliance";
import Burndown from "@/pages/Burndown";
import ClientSignOff from "@/pages/ClientSignOff";
import Timesheets from "@/pages/Timesheets";
import Goals from "@/pages/Goals";
import DailyStandup from "@/pages/DailyStandup";
import TimeTracking from "@/pages/TimeTracking";
import Account from "@/pages/Account";
import ClientPortal from "@/pages/ClientPortal";
import AdminPanel from "@/pages/AdminPanel";
import ActivityPage from "@/pages/ActivityPage";
import ChangeRequests from "@/pages/ChangeRequests";
import ResourceCalendar from "@/pages/ResourceCalendar";
import ProjectPipeline from "@/pages/ProjectPipeline";
import SOWPipeline from "@/pages/SOWPipeline";
import Intelligence from "@/pages/Intelligence";
import HealthScore from "@/pages/HealthScore";
import Documents from "@/pages/Documents";
import Marketing from "@/pages/Marketing";
import Retros from "@/pages/Retros";
import ClientComms from "@/pages/ClientComms";
import PurchaseOrders from "@/pages/PurchaseOrders";
import Bills from "@/pages/Bills";
import Catalog from "@/pages/Catalog";
import Retainers from "@/pages/Retainers";
import TimeOff from "@/pages/TimeOff";
import CustomFieldsAdmin from "@/pages/CustomFieldsAdmin";
import Dashboards from "@/pages/Dashboards";
import Automations from "@/pages/Automations";
import SSOSettings from "@/pages/SSOSettings";
import SCIMSettings from "@/pages/SCIMSettings";
import HelpCenter from "@/pages/HelpCenter";
import LiveSupport from "@/pages/LiveSupport";
import PublicHelpCenter from "@/pages/PublicHelpCenter";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Careers from "@/pages/Careers";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Dpa from "@/pages/Dpa";
import SupportQueue from "@/pages/SupportQueue";
import StatusPages from "@/pages/StatusPages";
import PublicStatus from "@/pages/PublicStatus";
import TaskBundles from "@/pages/TaskBundles";
import Planner from "@/pages/Planner";
import TaskMatrix from "@/pages/TaskMatrix";
import SavedViews from "@/pages/SavedViews";
import Quotes from "@/pages/Quotes";
import CompanyBudgets from "@/pages/CompanyBudgets";
import PDFSettings from "@/pages/PDFSettings";
import MCPSettings from "@/pages/MCPSettings";
import Entities from "@/pages/Entities";
import PDFTemplates from "@/pages/PDFTemplates";
import ActivityTypes from "@/pages/ActivityTypes";
import Permissions from "@/pages/Permissions";
import CustomModules from "@/pages/CustomModules";
import CustomModuleDetail from "@/pages/CustomModuleDetail";
import AppShell from "@/components/app/AppShell";
import PoweredByBadge from "@/components/app/PoweredByBadge";
import CrispChat from "@/components/app/CrispChat";
import { PresenceProvider } from "@/lib/presence-context";

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#030712] text-white p-8">
          <div className="max-w-lg w-full">
            <div className="text-[#FF007A] text-xs font-mono uppercase tracking-widest mb-3">Application error</div>
            <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
            <pre className="text-xs bg-[#0D1117] border border-[#1F2937] rounded p-4 overflow-auto text-red-400 mb-6 whitespace-pre-wrap">
              {this.state.error?.message || String(this.state.error)}
            </pre>
            <button
              onClick={() => { this.setState({ error: null }); window.location.href = "/dashboard"; }}
              className="px-4 py-2 bg-[#00F5D4] text-[#030712] font-bold rounded text-sm"
            >
              Go to dashboard
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030712]">
        <div className="text-xs uppercase tracking-[0.25em] text-[#7d8590]" data-testid="loading-indicator">
          Loading workspace…
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  return (
    <PresenceProvider>
      <AppShell>{children}</AppShell>
    </PresenceProvider>
  );
};

const Router = () => {
  const location = useLocation();
  const { user } = useAuth();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Show the "Powered by CombinedWork" badge on public/marketing surfaces only.
  // Hidden inside the authenticated app — the brand already lives in the sidebar there.
  const showBadge = !user;
  return (
    <>
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/dpa" element={<Dpa />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/workspace" element={<Protected><Workspace /></Protected>} />
      <Route path="/workspace/:projectId" element={<Protected><ProjectDetail /></Protected>} />
      <Route path="/workspace/:projectId/board" element={<Protected><AgileBoard /></Protected>} />
      <Route path="/workspace/:projectId/raid" element={<Protected><RaidPage /></Protected>} />
      <Route path="/workspace/:projectId/meetings" element={<Protected><Meetings /></Protected>} />
      <Route path="/workspace/:projectId/sprints" element={<Protected><Sprints /></Protected>} />
      <Route path="/workspace/:projectId/dependencies" element={<Protected><Dependencies /></Protected>} />
      <Route path="/workspace/:projectId/gantt" element={<Protected><Gantt /></Protected>} />
      <Route path="/workspace/:projectId/standup" element={<Protected><DailyStandup /></Protected>} />
      <Route path="/workspace/:projectId/chat" element={<Protected><ProjectChat /></Protected>} />
      <Route path="/workspace/:projectId/wiki" element={<Protected><Wiki /></Protected>} />
      <Route path="/workspace/:projectId/report" element={<Protected><ProjectReport /></Protected>} />
      <Route path="/workspace/:projectId/change-requests" element={<Protected><ChangeRequests /></Protected>} />
      <Route path="/reporting" element={<Protected><Reporting /></Protected>} />
      <Route path="/templates" element={<Protected><Templates /></Protected>} />
      <Route path="/deals" element={<Protected><Deals /></Protected>} />
      <Route path="/team" element={<Protected><Team /></Protected>} />
      <Route path="/capacity" element={<Protected><Capacity /></Protected>} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/sign-off/:token" element={<ClientSignOff />} />
      <Route path="/client/:token" element={<ClientPortal />} />
      <Route path="/timesheets" element={<Protected><Timesheets /></Protected>} />
      <Route path="/goals" element={<Protected><Goals /></Protected>} />
      <Route path="/time-tracking" element={<Protected><TimeTracking /></Protected>} />
      <Route path="/account" element={<Protected><Account /></Protected>} />
      <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
      <Route path="/invoices/aging" element={<Protected><AgingReport /></Protected>} />
      <Route path="/invoices/recurring" element={<Protected><RecurringInvoices /></Protected>} />
      <Route path="/invoices/:invoiceId" element={<Protected><InvoiceDetail /></Protected>} />
      <Route path="/my-tasks" element={<Protected><MyTasks /></Protected>} />
      <Route path="/marketplace" element={<Protected><Marketplace /></Protected>} />
      <Route path="/integrations" element={<Protected><Integrations /></Protected>} />
      <Route path="/portfolio" element={<Protected><Portfolio /></Protected>} />
      <Route path="/governance" element={<Protected><Governance /></Protected>} />
      <Route path="/contacts" element={<Protected><Contacts /></Protected>} />
      <Route path="/compliance" element={<Protected><Compliance /></Protected>} />
      <Route path="/burndown" element={<Protected><Burndown /></Protected>} />
      <Route path="/assistant" element={<Protected><Assistant /></Protected>} />
      <Route path="/billing" element={<Protected><Pricing /></Protected>} />
      <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/admin" element={<Protected><AdminPanel /></Protected>} />
      <Route path="/activity" element={<Protected><ActivityPage /></Protected>} />
      <Route path="/resource-calendar" element={<Protected><ResourceCalendar /></Protected>} />
      <Route path="/pipeline" element={<Protected><ProjectPipeline /></Protected>} />
      <Route path="/sow" element={<Protected><SOWPipeline /></Protected>} />
      <Route path="/intelligence" element={<Protected><Intelligence /></Protected>} />
      <Route path="/health-score" element={<Protected><HealthScore /></Protected>} />
      <Route path="/documents" element={<Protected><Documents /></Protected>} />
      <Route path="/marketing" element={<Protected><Marketing /></Protected>} />
      <Route path="/retros" element={<Protected><Retros /></Protected>} />
      <Route path="/client-comms" element={<Protected><ClientComms /></Protected>} />
      <Route path="/purchase-orders" element={<Protected><PurchaseOrders /></Protected>} />
      <Route path="/bills" element={<Protected><Bills /></Protected>} />
      <Route path="/catalog" element={<Protected><Catalog /></Protected>} />
      <Route path="/retainers" element={<Protected><Retainers /></Protected>} />
      <Route path="/time-off" element={<Protected><TimeOff /></Protected>} />
      <Route path="/custom-fields" element={<Protected><CustomFieldsAdmin /></Protected>} />
      <Route path="/dashboards" element={<Protected><Dashboards /></Protected>} />
      <Route path="/automations" element={<Protected><Automations /></Protected>} />
      <Route path="/sso-settings" element={<Protected><SSOSettings /></Protected>} />
      <Route path="/scim-settings" element={<Protected><SCIMSettings /></Protected>} />
      <Route path="/help-center" element={<Protected><HelpCenter /></Protected>} />
      <Route path="/live-support" element={<Protected><LiveSupport /></Protected>} />
      <Route path="/help" element={<PublicHelpCenter />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/support-queue" element={<Protected><SupportQueue /></Protected>} />
      <Route path="/status-pages" element={<Protected><StatusPages /></Protected>} />
      <Route path="/status/:slug" element={<PublicStatus />} />
      <Route path="/task-bundles" element={<Protected><TaskBundles /></Protected>} />
      <Route path="/planner" element={<Protected><Planner /></Protected>} />
      <Route path="/task-matrix" element={<Protected><TaskMatrix /></Protected>} />
      <Route path="/saved-views" element={<Protected><SavedViews /></Protected>} />
      <Route path="/quotes" element={<Protected><Quotes /></Protected>} />
      <Route path="/company-budgets" element={<Protected><CompanyBudgets /></Protected>} />
      <Route path="/pdf-settings" element={<Protected><PDFSettings /></Protected>} />
      <Route path="/mcp-settings" element={<Protected><MCPSettings /></Protected>} />
      <Route path="/entities" element={<Protected><Entities /></Protected>} />
      <Route path="/pdf-templates" element={<Protected><PDFTemplates /></Protected>} />
      <Route path="/activity-types" element={<Protected><ActivityTypes /></Protected>} />
      <Route path="/permissions" element={<Protected><Permissions /></Protected>} />
      <Route path="/custom-modules" element={<Protected><CustomModules /></Protected>} />
      <Route path="/custom-modules/:moduleId" element={<Protected><CustomModuleDetail /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
      {showBadge && <PoweredByBadge />}
      <CrispChat />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <ErrorBoundary>
              <Router />
            </ErrorBoundary>
            <Toaster position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
